/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { buildImageUrl, n } from "../utils/theme1Helpers";
import { updateProductVariantSafe, isCustomerLoggedIn } from "../../utils/storeApi";
import { formatNumber } from "utils/formatNumber";
import Swal from "sweetalert2";
import AuthModal from "../../components/AuthModal";

export default function ProductCard({ p, theme, viewMode, handleOpenProduct, handleAddToCart }) {
  const [selectedColorHex, setSelectedColorHex] = useState(null);
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(null);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;

  const colorGroups = useMemo(() => {
    if (!p?.variants) return {};
    const groups = {};
    p.variants.forEach(v => {
      const hex = v.color?.hex || v.color?.hexCode || "#333";
      if (!groups[hex]) {
        groups[hex] = { hex, variants: [], name: v.variantName || v.color?.name || "" };
      }
      groups[hex].variants.push(v);
    });
    return groups;
  }, [p]);

  const uniqueColors = useMemo(() => Object.values(colorGroups), [colorGroups]);

  const hasStockInGroup = (group) => {
    if (!group?.variants?.length) return false;
    return group.variants.some(v => {
      const vSizes = v.options || v.sizes || [];
      if (vSizes.length === 0) return n(v.stockQty) > 0;
      const hasSizeStock = vSizes.some(s => n(s.stock || s.stockQty) > 0);
      return hasSizeStock || n(v.stockQty) > 0;
    });
  };

  useEffect(() => {
    if (uniqueColors.length > 0) {
      const firstColor = uniqueColors.find(c => hasStockInGroup(c)) || uniqueColors[0];
      if (!selectedColorHex || !colorGroups[selectedColorHex]) {
        setSelectedColorHex(firstColor.hex);
      }
      const activeGroup = colorGroups[selectedColorHex] || firstColor;
      const allSizes = [];
      activeGroup.variants.forEach(v => {
        const vSizes = v.options || v.sizes || [];
        vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
      });
      const firstInStock = allSizes.find(s => s.stock > 0) || allSizes[0];
      if (firstInStock && !selectedSizeLabel) {
        setSelectedSizeLabel(firstInStock.label);
      }
    }
  }, [p, uniqueColors, selectedColorHex, colorGroups, selectedSizeLabel]);

  const activeColorGroup = colorGroups[selectedColorHex] || uniqueColors[0];

  const availableSizes = useMemo(() => {
    if (!activeColorGroup) return [];
    const sizeMap = new Map();
    activeColorGroup.variants.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => {
        const label = s.label || s.size;
        let stock = n(s.stock || s.stockQty);
        if (stock <= 0 && n(v.stockQty) > 0) stock = n(v.stockQty);
        if (!sizeMap.has(label)) {
          sizeMap.set(label, { label, stock, variant: v });
        } else {
          const existing = sizeMap.get(label);
          existing.stock += stock;
        }
      });
    });
    return Array.from(sizeMap.values());
  }, [activeColorGroup]);

  const activeSizeObj = useMemo(() => {
    return availableSizes.find(s => s.label === selectedSizeLabel) || availableSizes[0];
  }, [availableSizes, selectedSizeLabel]);

  const currentVariant = activeSizeObj?.variant || activeColorGroup?.variants?.[0];

  const priceDetails = useMemo(() => {
    if (!currentVariant) return { base: 0, discount: 0, final: 0 };

    const basePrice = Number(currentVariant.price || 0);
    const preCalculatedDiscount = Number(currentVariant.discount || 0);
    const preCalculatedFinal = Number(currentVariant.finalPrice || (basePrice - preCalculatedDiscount));

    const dValue = Number(p.discountValue || 0);
    const dType = Number(p.discountType || 0);

    let discountAmount = preCalculatedDiscount;
    let finalPrice = preCalculatedFinal;

    if (discountAmount === 0 && dValue > 0) {
      if (dType === 1) {
        discountAmount = (basePrice * dValue) / 100;
      } else {
        discountAmount = dValue;
      }
      finalPrice = basePrice - discountAmount;
    }

    return {
      base: basePrice,
      discount: discountAmount,
      final: Math.max(0, finalPrice)
    };
  }, [currentVariant, p.discountValue, p.discountType]);

  const displayImage = useMemo(() => {
    const src = currentVariant?.imageUrl || p.mainImage || p.mainImageUrl;
    if (!src) return "https://placehold.co/300x200?text=No+Image";
    return buildImageUrl(src);
  }, [currentVariant, p.mainImage, p.mainImageUrl]);

  const isOutOfStock = useMemo(() => {
    if (availableSizes.length === 0) return Number(currentVariant?.stockQty || 0) <= 0;
    return availableSizes.every(s => s.stock <= 0);
  }, [availableSizes, currentVariant]);

  const isProductOutOfStock = useMemo(() => {
    if (!uniqueColors.length) return Number(currentVariant?.stockQty || 0) <= 0;
    return uniqueColors.every(c => !hasStockInGroup(c));
  }, [uniqueColors, currentVariant]);

  const showOutOfStockAlert = () => {
    Swal.fire({
      icon: "info",
      title: "انتهى من المخزون",
      text: "هذا المنتج غير متوفر حالياً"
    });
  };

  const handleOpenDetails = () => {
    if (isProductOutOfStock) {
      showOutOfStockAlert();
      return;
    }
    handleOpenProduct({ productId: p.id, variantId: currentVariant?.variantId });
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) { showOutOfStockAlert(); return; }
    if (!isCustomerLoggedIn()) { setIsAuthOpen(true); return; }

    if (!currentVariant) {
      Swal.fire({ title: 'تنبيه', text: 'يرجى اختيار المنتج أولاً', icon: 'warning' });
      return;
    }

    const hex = currentVariant.color?.hex || currentVariant.color?.hexCode || "#000000";

    handleAddToCart({
      variantId: currentVariant.variantId,
      price: priceDetails.final,
      discount: priceDetails.discount,
      color: hex,
      colorName: hex,
      size: selectedSizeLabel || "",
      stockQty: activeSizeObj ? activeSizeObj.stock : Number(currentVariant.stockQty || 0)
    }, 1);
  };

  const handleColorSelect = (hex) => {
    setSelectedColorHex(hex);
    const group = colorGroups[hex];
    const allSizes = [];
    group?.variants?.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
    });
    const firstInStock = allSizes.find(s => s.stock > 0) || allSizes[0];
    setSelectedSizeLabel(firstInStock?.label || null);
  };

  return (
    <>
      <div style={{
        background: "white",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: isProductOutOfStock ? "none" : "0 4px 20px rgba(0,0,0,0.06)",
        border: isProductOutOfStock ? "1px solid #e5e7eb" : "1px solid #f0f0f0",
        display: viewMode === "list" ? "flex" : "block",
        flexDirection: viewMode === "list" ? "row" : "column",
        height: "100%",
        minHeight: viewMode === "list" ? (isMobile ? "auto" : 220) : "auto",
        transition: "all 0.3s ease",
        position: "relative",
        alignItems: viewMode === "list" && isMobile ? "center" : "stretch",
        gap: viewMode === "list" && isMobile ? "10px" : "0",
        opacity: isProductOutOfStock ? 0.8 : 1
      }}
        className="product-card-container"
      >

        {/* Image Section */}
        <div style={{
          position: "relative",
          height: viewMode === "list" ? (isMobile ? "120px" : "220px") : "240px",
          minHeight: viewMode === "list" ? (isMobile ? "110px" : "180px") : "180px",
          width: viewMode === "list" ? (isMobile ? "110px" : "40%") : "100%",
          minWidth: viewMode === "list" && isMobile ? "110px" : undefined,
          background: "#F9F9F9",
          cursor: "pointer",
          overflow: "hidden",
          padding: viewMode === "list" ? (isMobile ? "8px" : "15px") : "15px",
          borderRadius: viewMode === "list" && isMobile ? "12px" : "0"
        }} onClick={handleOpenDetails}>
          <img
            src={displayImage}
            alt={p.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: viewMode === "list" ? "cover" : "contain",
              padding: viewMode === "list" ? (isMobile ? "0" : "8px") : "15px",
              transition: "transform 0.5s ease"
            }}
            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/300x200?text=No+Image"; }}
            className="product-image"
          />
          {priceDetails.discount > 0 && (
            <div style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "#E74C3C",
              color: "white",
              padding: "5px 10px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "bold",
              boxShadow: "0 2px 10px rgba(231, 76, 60, 0.3)",
              zIndex: 2
            }}>
              خصم {p.discountType === 1 ? `${p.discountValue}%` : formatNumber(p.discountValue)}
            </div>
          )}
        </div>

        {/* Content Section */}
        <div style={{
          padding: viewMode === "list" ? (isMobile ? "12px" : "20px 25px") : "15px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: viewMode === "list" && isMobile ? 8 : 0
        }}>
          <h3 style={{
            fontSize: viewMode === "list" ? (isMobile ? "16px" : "20px") : "16px",
            fontWeight: "700",
            marginBottom: (p.categoryName || p.subcategoryName) ? "4px" : "12px",
            color: "#2D3436"
          }}>
            {p.name}
          </h3>

          {(p.category || p.subCategory) && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px", flexWrap: "wrap" }}>
              {p.category && (
                <span style={{ fontSize: "11px", fontWeight: "800", color: theme.primary, textTransform: "uppercase" }}>
                  {p.category}
                </span>
              )}
              {p.category && p.subCategory && <span style={{ color: "#EEE", fontSize: "12px" }}>-</span>}
              {p.subCategory && (
                <span style={{ fontSize: "11px", fontWeight: "600", color: "#999" }}>
                  {p.subCategory}
                </span>
              )}
            </div>
          )}

          {/* Tags */}
          {p.tags && p.tags.length > 0 && (
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              marginBottom: "12px"
            }}>
              {p.tags.map((tag, idx) => (
                <span
                  key={idx}
                  style={{
                    fontSize: "10px",
                    fontWeight: "700",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    background: theme.primary + "15",
                    color: theme.primary,
                    border: `1px solid ${theme.primary}22`
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Color Selection */}
          {uniqueColors.length > 0 && (
            <div style={{ marginBottom: "15px" }}>
              <div style={{
                display: viewMode === "list" && isMobile ? "grid" : "flex",
                gridTemplateColumns: viewMode === "list" && isMobile ? "repeat(2, 36px)" : undefined,
                gap: "10px",
                flexWrap: viewMode === "list" && isMobile ? "nowrap" : "wrap"
              }}>
                {uniqueColors.map((c) => {
                  const isThisSelected = selectedColorHex === c.hex;
                  return (
                    <button
                      key={c.hex}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleColorSelect(c.hex);
                      }}
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        background: c.hex,
                        border: isThisSelected ? `3px solid ${theme.primary}` : "1px solid #eee",
                        boxShadow: isThisSelected ? `0 0 0 1px ${theme.primary}22` : "none",
                        cursor: "pointer",
                        transform: isThisSelected ? "scale(1.15)" : "scale(1)",
                        transition: "all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                      }}
                      title={c.name}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {availableSizes.length > 0 && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{
                display: viewMode === "list" && isMobile ? "grid" : "flex",
                gridTemplateColumns: viewMode === "list" && isMobile ? "repeat(2, max-content)" : undefined,
                justifyContent: "flex-start",
                flexWrap: viewMode === "list" && isMobile ? "wrap" : "wrap",
                gap: "6px"
              }}>
                {availableSizes.map((opt, idx) => {
                  const isActive = selectedSizeLabel === opt.label;
                  return (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (opt.stock <= 0) {
                          showOutOfStockAlert();
                          return;
                        }
                        setSelectedSizeLabel(opt.label);
                      }}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        fontWeight: "600",
                        border: isActive ? `2px solid ${theme.primary}` : "1px solid #f0f0f0",
                        background: isActive ? theme.primary : "#F8F9FA",
                        color: isActive ? "white" : (opt.stock <= 0 ? "#ccc" : "#444"),
                        cursor: opt.stock <= 0 ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease"
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price and Add to Cart */}
          <div style={{
            marginTop: "auto",
            display: viewMode === "list" ? "flex" : "block",
            flexDirection: viewMode === "list" && isMobile ? "row" : undefined,
            alignItems: "center",
            justifyContent: "space-between",
            gap: viewMode === "list" && isMobile ? "10px" : "20px",
            flexWrap: viewMode === "list" && isMobile ? "wrap" : "nowrap"
          }}>
            <div style={{
              display: "flex",
              flexDirection: viewMode === "list" && isMobile ? "row" : "column",
              gap: viewMode === "list" && isMobile ? "6px" : "4px",
              alignItems: viewMode === "list" && isMobile ? "center" : "flex-start",
              justifyContent: "center",
              marginBottom: viewMode === "list" ? "0" : "12px",
              flexWrap: viewMode === "list" && isMobile ? "wrap" : "nowrap",
              minWidth: viewMode === "list" && isMobile ? "120px" : "auto"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: viewMode === "list" ? (isMobile ? "18px" : "24px") : "20px", fontWeight: "900", color: theme.primary }}>
                  {formatNumber(priceDetails.final)} IQD
                </span>
                {priceDetails.discount > 0 && (
                  <span style={{ textDecoration: "line-through", color: "#bbb", fontSize: "14px", fontWeight: "400" }}>
                    {formatNumber(priceDetails.base)}
                  </span>
                )}
              </div>
              {isOutOfStock && (
                <span style={{ color: "#E74C3C", fontSize: "12px", fontWeight: "600" }}>
                  غير متوفر حالياً
                </span>
              )}
            </div>

            <button
              disabled={isOutOfStock}
              onClick={handleAddClick}
              style={{
                width: viewMode === "list" && isMobile ? "auto" : viewMode === "list" ? "auto" : "100%",
                padding: viewMode === "list" ? (isMobile ? "10px 16px" : "12px 30px") : "14px",
                borderRadius: "14px",
                background: isOutOfStock ? "#f5f5f5" : theme.primary,
                color: isOutOfStock ? "#aaa" : "white",
                fontWeight: "700",
                fontSize: "15px",
                border: "none",
                cursor: isOutOfStock ? "not-allowed" : "pointer",
                boxShadow: isOutOfStock ? "none" : `0 8px 20px ${theme.primary}44`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                whiteSpace: "nowrap"
              }}
              className="add-to-cart-btn"
            >
              {isOutOfStock ? "نفذت الكمية" : "إضافة للسلة"}
            </button>
          </div>
        </div>
      </div >
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} tenantId={p.tenantId} theme={theme} />
    </>
  );
}
